import { IField } from '@/components/Form/data';
import Services from '@/services';
import { getAnnexPolicyId, isAnnexMode } from '@/store/Annex/annexMode';
import { EnumResponse } from '@/types/base';
import { Address, CreateCustomerCommand_Request } from '@/types/models';
import { history } from '@umijs/max';
import type { ColGroupDef, RowNode, SetFilterValuesFuncParams } from 'ag-grid-community';
import type { FormInstance } from 'antd';
import Decimal from 'decimal.js';
import parsePhoneNumber from 'libphonenumber-js';
import moment from 'moment';
import numeral from 'numeral';

export const capitalizeFirstLetter: (string: string) => string = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const lowercaseFirstLetter = (str: string) => {
    return str.charAt(0).toLowerCase() + str.slice(1);
};

export const formErrorHandler: (error: any) => {
    name: any;
    errors: any[];
}[] = (error: any) => {
    if (!error?.errors) return [];
    const errors: { key: string; content: string }[] = error?.errors ?? [];
    return errors.map((error: any) => ({
        name: lowercaseFirstLetter(error.key),
        errors: [error.content],
    }));
};

export const formatContent: (value: any) => any = (value: any) => {
    return value?.replace(/<\/?[^>]+(>|$)/g, '');
};

export function getBase64(file: any): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });
}

export const complexColumnKeyValue: (key: string) => {
    keyCreator: ({ value }: any) => any;
    valueFormatter: ({ data }: any) => any;
} = (key: string) => {
    return {
        keyCreator: ({ value }: any) => value,
        valueFormatter: ({ data }: any) => data[key],
    };
};

export const redirectToEntity: (pathname: string, id: string) => void = (pathname: string, id: string) => {
    window.open(`${pathname}?id=${id}`, '_blank');
};

export const canConvertToDate: (value: string | number) => boolean = (value: string | number) => {
    return moment(value, moment.ISO_8601, true).isValid() && typeof value === 'string' && value.includes('-');
};

export const validateCustomFields: (form: FormInstance) => Promise<{}> = async (form: FormInstance) => {
    const values = await form.validateFields();
    const formatted = {};
    for (const property in values) {
        const properties = values[property];
        if (typeof properties === 'object' && !canConvertToDate(properties) && !properties?._isAMomentObject) {
            formatted[property] = properties?.key;
            continue;
        }
        formatted[property] = properties;
    }
    return formatted;
};

export const toPercentageFormat: (numberValue: number | string, precision?: number) => string = (numberValue, precision = 2) => {
    let zeros: string = '';

    for (var i = 0; i < new Array(precision).length; i++) {
        zeros += '0';
    }

    return `${numeral(Number(numberValue) * 100).format(`0,0.[${zeros}]`)}%`;
};

export function* chunk(arr: any, n: number): Generator<any, void, unknown> {
    for (let i = 0; i < arr.length; i += n) {
        yield arr.slice(i, i + n);
    }
}

export const toCurrency: (value: number) => string = (value: number) => {
    return numeral(value).format('0,0.00');
};

export const getQueryUrl = (key: string = 'id'): string => {
    const params = new Proxy(new URLSearchParams(window.location.search), {
        get: (searchParams, prop: string) => searchParams.get(prop),
    });
    return params[key] ?? '';
};

export const getRandomInt: (min: number, max: number) => number = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const pushIdToUrl: (id: string) => void = (id: string) => {
    const { pathname, search } = window.location;
    history.push(`${pathname}${search ? search + '&' : '?'}id=${id}`);
};

export const removeIdFromUrl: () => void = () => {
    if (isAnnexMode()) return;

    const { pathname, search } = window.location;
    const params = new URLSearchParams(search);
    params.delete('id');
    params.delete('editId');
    const cleanedUrl = params.toString();
    history.push(pathname, cleanedUrl ? `?${cleanedUrl}` : '');
};

export const removeActionColumn: (columns: ColGroupDef[]) => ColGroupDef[] = (columns) => {
    const newColumns: ColGroupDef[] = columns.map((col: ColGroupDef, index: number) => {
        if (index === columns.length - 1) col.children.pop();
        return col;
    });

    return newColumns;
};

export const getInitials = (value: string) => {
    if (!value) return '...';
    if (/\s/g.test(value)) {
        return value
            .replace(/['",()]+/g, '')
            .split(' ')
            .map((part: string) => part[0]?.toUpperCase())
            .join()
            .replace(/,/g, '');
    }
    return value[0]?.toUpperCase().replace(/['",()]+/g, '');
};

export const getNameFields: (contactType: string) => IField[] = (contactType) => {
    if (contactType === 'Person') {
        return [
            {
                component: 'Input',
                name: 'firstName',
                label: 'First Name',
                placeholder: 'First Name',
                span: 12,
                rules: [{ required: true, message: 'errors.required.firstName' }],
            },
            {
                component: 'Input',
                name: 'lastName',
                label: 'Last Name',
                placeholder: 'Last Name',
                span: 12,
                rules: [{ required: true, message: 'errors.required.lastName' }],
            },
        ];
    }
    return [
        {
            component: 'Input',
            name: 'displayName',
            label: 'Name',
            placeholder: 'Name',
            span: 12,
            rules: [{ required: true, message: 'errors.required.name' }],
        },
    ];
};

export const getFormattedAddress = (data: any) => {
    const formatted = data;
    const { country, city, postalCode, streetAddress, streetAddress2, latitude, longitude } = formatted;
    formatted['address'] = {
        country,
        city,
        postalCode,
        streetAddress,
        streetAddress2,
        latitude,
        longitude,
    };
    return formatted;
};

export const getFormattedCustomer: (data: any) => CreateCustomerCommand_Request = (data) => {
    const {
        country,
        city,
        postalCode,
        streetAddress,
        streetAddress2,
        latitude,
        longitude,
        fathersName,
        birthday,
        genderType,
        passportNumber,
        isActive,
        businessDescription,
        administrator,
        organizationLegalFormType,
        organizationBusinessSizeType,
        organizationOwnershipType,
        organizationSectorType,
        email,
    } = data;

    const formatted: CreateCustomerCommand_Request = {
        ...data,
        address: {
            country: country || 'AL',
            city,
            postalCode,
            streetAddress,
            streetAddress2,
            latitude,
            longitude,
        },
        displayName: data.displayName || `${data.firstName} ${data.lastName}`,
        personData: {
            fathersName,
            ...(!!birthday ? { birthday } : {}),
            genderType: genderType || '',
            passportNumber,
        },
        ...(data.contactType === 'Organization' ? { firstName: data.displayName } : {}),
        organizationData: {
            isActive: isActive || false,
            businessDescription,
            administrator,
            organizationLegalFormType,
            organizationBusinessSizeType,
            organizationOwnershipType,
            organizationSectorType,
        },
        email: !!email ? email : null,
    };

    return formatted;
};

function IsNullOrWhiteSpace(input: string) {
    return !input || !input.trim();
}

export const toCurrencyStandardFormatCurrency: (input: number | Decimal, currency?: string, showDecimalPlaces?: boolean, currencyFirst?: boolean) => string = (
    input,
    currency = '',
    showDecimalPlaces = true,
    currencyFirst = false
) => {
    const format = '0,0' + (showDecimalPlaces ? '.00' : '');

    const formattedNumber = numeral(input).format(format);

    if (currencyFirst) {
        return IsNullOrWhiteSpace(currency) ? '' : `${currency} ` + formattedNumber;
    }

    return `${formattedNumber.toString()}${IsNullOrWhiteSpace(currency) ? '' : ' ' + currency}`;
};

export const toCurrencyFullPrecision: (input: number | Decimal, currency?: string) => string = (input, currency = '') => {
    const decimal = input instanceof Decimal ? input : new Decimal(String(input ?? 0));
    const isNegative = decimal.isNegative();
    const [intPart, decPart] = decimal.abs().toFixed().split('.');
    const intWithThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const fractional = decPart ? `.${decPart}` : '.00';
    const sign = isNegative ? '-' : '';
    const formatted = `${sign}${intWithThousands}${fractional}`;
    return `${formatted}${IsNullOrWhiteSpace(currency) ? '' : ' ' + currency}`;
};

export const toDateStandardFormat: (dateTime: moment.MomentInput, includeTime?: boolean) => string = (dateTime, includeTime = false) => {
    if (!dateTime) return '-';
    return moment(dateTime).format('DD.MM.YYYY' + (includeTime ? ', h:mm:ss' : ''));
};

export const prepareFieldsToInsert = (data: any) => {
    for (const key in data) {
        const value = data[key];
        if (canConvertToDate(value)) {
            data[key] = moment(value);
        }
    }
    const getAddressDetails = () => (typeof data.address === 'object' ? data.address : {});

    const getNameDetails = () => {
        if (data.contactType === 'Person') {
            if (!!data.reportName && !!data.reportName?.trim()) {
                const [firstName, lastName] = data.reportName.split('|');
                return {
                    firstName,
                    lastName,
                };
            }

            if (!!data.displayName) {
                const fullName = data.displayName.split(' ');

                return {
                    firstName: fullName?.[0] || '',
                    lastName: fullName?.filter((_: string, index: number) => index !== 0)?.join(' ') || '', // remove the first string, all the other string belongs to last name
                };
            }

            return {
                firstName: data.firstName,
                lastName: data.lastName,
            };
        }

        return {
            displayName: data.displayName || '',
        };
    };

    return {
        ...data,
        ...getAddressDetails(),
        ...getNameDetails(),
    };
};

export const getFreeFormAddress: (address?: Address) => string = (address = undefined) => {
    if (!address) return '';
    return `${address.streetAddress ?? ''}, ${address.city ?? ''}, ${address.postalCode ?? ''}`;
};

export const getSetFilterAgGridOptions = async (params: SetFilterValuesFuncParams, enumName: string) => {
    const response: EnumResponse[] = await Services.getSpecificEnum(enumName);

    if (response === null) return;

    params.success(response.map((option: EnumResponse) => option.key));
};

export const getRanges = (isPercentage: boolean) => {
    if (isPercentage)
        return {
            max: 1,
            min: 0,
            percentage: isPercentage,
        };
    return {
        min: 0,
        currency: '',
    };
};

// Toggles the expansion of a row (node) - eg. Group Rows
export const toggleNodeExpansion: (node: RowNode | undefined | null) => void = (node) => node && node.setExpanded(!node.expanded);

export const formatPhoneNumber = (phoneNumber: string) => {
    const defaultCountry = 'AL';
    try {
        const parsedNumber = parsePhoneNumber(String(phoneNumber), defaultCountry);
        if (parsedNumber?.isValid) return parsedNumber.number;
    } catch (error) {
        console.error('Error parsing phone number:', error);
    }

    return phoneNumber;
};

export function handleBack() {
    return window.history.back();
}

export const isMobile = () => {
    return typeof window !== 'undefined' && window.innerWidth < 768;
};

export function isMobileDevice() {
    return /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop|BlackBerry|webOS/i.test(navigator.userAgent);
}

// Keep in mind this will work till the function 'startRenewal' end. after that, this function will delete the query param and this check will not work
export const isRenewalPolicy = () => {
    return isAnnexMode() || !!getQueryUrl() || !!getQueryUrl('editId');
};

// True only when the page was opened from the "Edit policy" action on a sold policy.
// Note: this only works until startRenewal/startEdit removes the query param.
export const isEditPolicy = () => {
    return isAnnexMode() || !!getQueryUrl('editId');
};

// Returns the policy id to prefill the form with, regardless of whether
// the page was opened in renewal mode (?id=) or edit mode (?editId=).
export const getRenewalOrEditPolicyId = () => {
    return getAnnexPolicyId() || getQueryUrl() || getQueryUrl('editId');
};

// Projection type for the draft-issue-request-projection endpoint.
// CurrentTerms is used when editing an existing policy (keep original dates/serial/etc.).
// Renewal is used when renewing a policy (backend shifts dates to the next period).
export const PROJECTION_TYPE = {
    CurrentTerms: 'CurrentTerms',
    Renewal: 'Renewal',
} as const;

export type ProjectionType = (typeof PROJECTION_TYPE)[keyof typeof PROJECTION_TYPE];

export const getProjectionType = (): ProjectionType => {
    return isEditPolicy() ? PROJECTION_TYPE.CurrentTerms : PROJECTION_TYPE.Renewal;
};

export const getDraftIssueRequestProjectionUrl = (policyId: string, projectionType: ProjectionType) => {
    return `/policystandard/draft-issue-request-projection/${policyId}/${projectionType}`;
};
